import {
  useControllableValue,
  useCreation,
  useDeepCompareEffect,
  useHover,
  useMemoizedFn,
} from 'ahooks';
import { type MouseEvent, useRef, useState } from 'react';
import {
  clone,
  createNode,
  getNodeDepth,
  getPathByValue,
  getPathLabel,
  getPathToNode,
} from './path-fns';
import type { CascaderTabsClickHandler, IProps } from './types';

const ACTIVE_TAB = 1;

const useCascader = ({
  value: controllValue,
  defaultValue,
  options: originOptions = [],
  onChange,
  visible: controllVisible,
  onVisibleChange,
  changeOnSelect,
  renderValue = getPathLabel,
}: IProps) => {
  const triggerRef = useRef<HTMLDivElement>(null);
  const isHovering = useHover(triggerRef);
  const [value, setValue] = useState(controllValue || defaultValue || []);
  const [visible, setVisible] = useControllableValue(
    { visible: controllVisible, onVisibleChange },
    {
      defaultValue: false,
      trigger: 'onVisibleChange',
    },
  );
  useDeepCompareEffect(() => {
    if (!controllVisible) {
      setValue(controllValue || []);
    }
  }, [controllValue, controllVisible]);

  const [activeTab, setActiveTab] = useState(value?.length || ACTIVE_TAB);

  const options = useCreation(
    () => clone(originOptions, createNode),
    [originOptions],
  );
  const valueText = useCreation(() => {
    const selectedPath = getPathByValue(options, value);
    return selectedPath?.length ? renderValue(selectedPath) : undefined;
  }, [options, value]);

  /**
   * 城市级联某一层级的子节点点击事件
   * @param node 点击的节点
   * @param level 当前的层级，从 1 开始计数
   */
  const onSelect: CascaderTabsClickHandler = async (node) => {
    const selectedOptions = getPathToNode(node);
    const newValue = selectedOptions.map((n) => n.adCode);
    setValue(newValue);

    const needClose = !node?.children?.length;
    const needTriggerChange = needClose || changeOnSelect;
    const level = getNodeDepth(node);
    const nextLevel = level + 1;

    if (!needClose) {
      setActiveTab(nextLevel);
    }

    if (needClose) {
      setVisible(false);
    }

    if (needTriggerChange) {
      onChange?.(newValue, selectedOptions);
    }
  };

  const onClear = (e: MouseEvent<HTMLSpanElement>) => {
    e.stopPropagation();
    setVisible(false);
    setActiveTab(ACTIVE_TAB);
    setValue([]);
    onChange?.([], []);
  };

  return {
    value,
    visible,
    setVisible,
    options,
    onSelect: useMemoizedFn(onSelect),
    activeTab,
    setActiveTab,
    valueText,
    triggerRef,
    isHovering,
    onClear: useMemoizedFn(onClear),
  };
};

export default useCascader;
